'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';
import { trpc } from '@/app/_trpc/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { pushAnalyticsEvent } from '@/lib/analyticsClientSide';
import { logger } from '@/lib/clientSideLogger';
import { loginSchema } from '@/schemas/loginSchemas';
export function TermsAndConditions() {
  const t = useTranslations('SignupForm');

  return (
    <p className='mt-2 text-center text-xs text-gray-500'>
      {t('bySigningUp')}{' '}
      <Link
        href='https://www.findhaven.org/user-agreement'
        className='text-blue-600 hover:underline'
        target='_blank'
        rel='noopener noreferrer'
      >
        {t('userAgreement')}
      </Link>{' '}
      {t('and')}{' '}
      <Link
        href='https://www.findhaven.org/privacy-policy'
        className='text-blue-600 hover:underline'
        target='_blank'
        rel='noopener noreferrer'
      >
        {t('privacyPolicy')}
      </Link>
    </p>
  );
}

export default function SignupForm({
  closePopup,
}: {
  closePopup?: () => void;
}) {
  const t = useTranslations('SignupForm');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const registerUser = trpc.auth.registerUser.useMutation({
    onSuccess: async () => {
      setIsLoading(false);
      closePopup?.();
      pushAnalyticsEvent('signup', {
        status: 'completed',
      });
      await signIn('credentials', {
        email,
        password,
        redirect: false,
      });
      router.push('/onboarding');
    },
    onError: (error) => {
      setIsLoading(false);
      if (error.data?.code === 'CONFLICT') {
        setError(t('userAlreadyExists'));
        logger('log', error.message);
      } else {
        setError(t('errorRegisteringUser'));
        const enhancedError = new Error(error.message);
        enhancedError.cause = error;
        logger('error', enhancedError);
      }
    },
  });
  useEffect(() => {
    pushAnalyticsEvent('signup', {
      status: 'start',
    });
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      setError(result.error.errors.map((err) => err.message).join(', '));
      setIsLoading(false);
      return;
    }

    registerUser.mutate({ email, password });
  };

  return (
    <div className='mx-auto w-full max-w-md space-y-6'>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='email' className='sr-only'>
            {t('emailLabel')}
          </Label>
          <Input
            id='email'
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete='username'
            placeholder={t('emailLabel')}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='password' className='sr-only'>
            {t('passwordLabel')}
          </Label>
          <Input
            id='password'
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete='current-password'
            placeholder={t('passwordLabel')}
          />
        </div>

        {error && <p className='text-red-500'>{error}</p>}

        <Button
          type='submit'
          loading={isLoading}
          disabled={isLoading}
          className='w-full bg-blue-600 hover:bg-blue-700'
        >
          {t('submitButton')}
        </Button>
        <TermsAndConditions />
      </form>
      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <span className='w-full border-t' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-background text-muted-foreground px-2'>
            {t('or')}
          </span>
        </div>
      </div>
      <Button
        variant='outline'
        className='w-full'
        onClick={() => signIn('google')}
      >
        <div className='flex w-full items-center justify-between'>
          <Image
            src='/images/google-logo.svg'
            alt='Google logo'
            width={20}
            height={20}
          />
          <span className='grow text-center'>{t('continueWithGoogle')}</span>
          <svg className='h-5 w-5 opacity-0' viewBox='0 0 24 24'>
            <path
              d='M12 5v14M5 12h14'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </Button>
      <p className='text-center text-sm'>
        {t('alreadyHaveAccount')}{' '}
        <Link
          href='/login'
          className='text-blue-600 hover:underline'
          onClick={() => closePopup && closePopup()}
        >
          {t('login')}
        </Link>
      </p>
    </div>
  );
}
